import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { ShowWarningNotification } from '@platform-frontend/util';
import { differenceInDays } from 'date-fns';
import { Observable, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { MSG_DISABLED_ACCOUNT } from '../notification-messages';
import { FetchDisabledDetails, GotoAcceptNewTerms, GotoDeactivatedAccount } from './disabled-details.actions';
import { DisabledDetailsState } from './disabled-details.state';
import { DisabledReason } from './model/disabled-reason';

@Injectable({
  providedIn: 'root'
})
export class DisabledAccountRedirectService {
  constructor(private store: Store) {}

  redirectDisabledUser(blockDeleted: boolean = false, clearNotifications?: boolean): Observable<boolean> {
    return this.store.dispatch(new FetchDisabledDetails()).pipe(
      switchMap(() => this.store.selectOnce(DisabledDetailsState.reason)),
      switchMap(reason => this.handleDisabledReason(reason, blockDeleted, clearNotifications))
    );
  }

  private handleDisabledReason(reason: DisabledReason | null, blockDeleted: boolean, clearNotifications?: boolean): Observable<boolean> {
    if (reason === 'outdatedTerms') {
      return this.store.dispatch(new GotoAcceptNewTerms({ clearNotifications })).pipe(map(() => true));
    } else if (reason === 'deletion' && blockDeleted) {
      return this.store.dispatch(new GotoDeactivatedAccount({ clearNotifications })).pipe(map(() => true));
    } else if (reason === 'deletion') {
      return combineLatest([this.store.selectOnce(DisabledDetailsState.deleteDate), this.store.selectOnce(DisabledDetailsState.disabledDate)]).pipe(
        switchMap(([deleteDate, disabledDate]) =>
          deleteDate && disabledDate ? this.store.dispatch(this.createWarningNotification(disabledDate, deleteDate)) : of(null)
        ),
        map(() => false)
      );
    } else {
      return of(false);
    }
  }

  private createWarningNotification(disabledDate: Date, deleteDate: Date): ShowWarningNotification {
    const days = differenceInDays(deleteDate, Date.now());
    return new ShowWarningNotification(MSG_DISABLED_ACCOUNT(disabledDate, days));
  }
}
